/** Sticky action bar for Customize on a platform `kind: process` row (E3,
 * spec 2026-09-18 §6). ConfigureBar's twin over processConfigStore: same
 * save → debounced validate → publish gate, same stale-response guard. A
 * finding is a button — it opens the Steps tab at the card that owns it. */
import { useState } from 'react'
import { useConfigureSession } from '@/hooks/useConfigureSession'
import { useProcessConfigStore } from '@/stores/processConfigStore'
import { Button } from '@/components/ui'
import { describeOverlayPath } from '@/utils/workflowConfigView'
import type { ConfigFinding } from '@/types/workflowConfig'
import { ConfigConflictBanner } from '../configure/ConfigConflictBanner'
import { PublishDialog } from '../configure/PublishDialog'
import { RebaseConflictsLine } from '../configure/RebaseConflictsLine'

// D9 (E2 spec): an instance pins its CONTRACT and reads parameters live at
// step entry — so, unlike an order publish, this one reaches running records.
const PUBLISH_NOTE =
  'These settings apply from the next step each record enters, including records already in progress. The steps themselves never change here.'

export function ProcessConfigureBar({
  workflowId,
  onReviewFinding,
  onReviewPath,
}: {
  workflowId: string
  onReviewFinding: (finding: ConfigFinding) => void
  /** SA-1410: opens the setting a keep-edits Reload let the published value win. */
  onReviewPath: (path: string) => void
}) {
  const store = useProcessConfigStore()
  const session = useConfigureSession(workflowId, store)
  const { validation, base } = store
  const [showPublish, setShowPublish] = useState(false)
  const { dirty, overlayEmpty, canPublish, validateFailed, conflict } = session

  // opaque base + primary tint overlay — sticky bar must not let content bleed through
  return (
    <div className="sticky bottom-0 z-10 space-y-2 rounded-xl border bg-card bg-linear-to-t from-primary/10 to-primary/10 border-primary/20 px-4 py-2">
      {validateFailed && (
        <p className="text-xs text-warning-strong">
          Couldn't check this configuration with the server — it will retry on your next change.
        </p>
      )}
      {validation && !validation.valid && (
        <ul className="space-y-0.5 text-xs text-destructive">
          {validation.findings.map((f, i) => {
            const d = describeOverlayPath(f.path, base)
            return (
              <li key={i}>
                <button
                  type="button"
                  title={f.path}
                  className={`underline underline-offset-2 ${d.resolved ? '' : 'font-mono'}`}
                  onClick={() => onReviewFinding(f)}
                >
                  {d.label}
                </button>
                : {f.message}
              </li>
            )
          })}
        </ul>
      )}
      {conflict && (
        <ConfigConflictBanner conflict={conflict} reloading={session.reloading} onReload={session.handleReload} />
      )}
      <RebaseConflictsLine
        paths={session.rebaseConflicts}
        describe={(path) => describeOverlayPath(path, base)}
        onReview={onReviewPath}
      />
      {session.saveError && <p className="text-xs text-destructive">{session.saveError}</p>}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-foreground">
          Customizing this process.{' '}
          {dirty ? <span className="font-medium">Unsaved changes.</span> : <span>Draft saved.</span>}
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => store.stopEditing()}>
            Exit
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!dirty || session.saving || conflict !== null}
            isLoading={session.saving}
            onClick={session.handleSave}
          >
            {session.saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            size="sm"
            disabled={!canPublish}
            title={conflict ? 'Reload first' : dirty ? 'Save the draft first' : overlayEmpty ? 'Nothing customized yet' : undefined}
            onClick={() => setShowPublish(true)}
          >
            Publish
          </Button>
        </div>
      </div>
      {showPublish && validation && (
        <PublishDialog
          workflowId={workflowId}
          diff={validation.diff}
          describe={(path) => describeOverlayPath(path, base)}
          note={PUBLISH_NOTE}
          basis={session.basis}
          onPublished={() => store.stopEditing()}
          onConflict={session.raiseConflict}
          onClose={() => setShowPublish(false)}
        />
      )}
    </div>
  )
}
